// The bus half of a bus stop's detail sheet: which routes serve it, and the
// next ten departures.
//
// TEN, AND ACROSS DAYS IF THAT IS WHAT IT TAKES. A campus stop is not a busy
// downtown corner: measured on the live service, one stop runs two buses just
// after midnight and then nothing for five hours; another has no service at all
// for the whole coming week. A panel that shows "the next few in the next hour"
// answers those with a blank, which reads as a broken app rather than as the
// bad news it is. The server walks the timetable forward until it has ten or
// runs out (see api/_transit.js), and this component's job is to make the gaps
// legible rather than hide them — hence the day headings and the plain
// statement when there is nothing.
//
// Which routes serve the stop is shown even when nothing is due, because that
// question has an answer at 3am and an empty departure list is not it.
import { useEffect, useRef, useState } from 'react';
import {
  clock, groupByDay, inMinutes, isImminent, lateness, stopRoutes, stopWhere,
} from '../lib/transit.js';

/** Winnipeg Transit's own badge colours, so a route looks like its schedule. */
function RouteBadge({ route }) {
  const style = {
    background: route.colour || '#37474f',
    color: route.textColour || '#fff',
  };
  return (
    <span className="pfa-bus-badge" style={style} title={route.name || route.number}>
      {route.number || route.key}
    </span>
  );
}

export default function BusStopDepartures({ stopKey, cachedRoutes, loadDepartures }) {
  const [state, setState] = useState({ loading: true });
  // Minutes-until has to keep counting down while the sheet is open, but the
  // departures themselves are a network call — so the clock ticks locally and
  // the data does not.
  const [, setTick] = useState(0);
  const liveRef = useRef(true);

  useEffect(() => {
    liveRef.current = true;
    if (!loadDepartures) {
      setState({ error: 'Bus times are not available here.' });
      return () => { liveRef.current = false; };
    }
    setState({ loading: true });
    loadDepartures(stopKey)
      .then(d => { if (liveRef.current) setState({ data: d }); })
      .catch(e => { if (liveRef.current) setState({ error: e.message || 'Bus times are unavailable right now.' }); });
    return () => { liveRef.current = false; };
  }, [stopKey, loadDepartures]);

  useEffect(() => {
    const id = setInterval(() => setTick(t => t + 1), 30000);
    return () => clearInterval(id);
  }, []);

  const data = state.data;
  const departures = data?.departures || [];
  // The cached route list from the snapshot is the fallback: it means the
  // badges are already right on the first paint, before the request lands, and
  // still right if the request fails.
  const routes = stopRoutes(data?.routes?.length ? data.routes : cachedRoutes, departures);
  const groups = groupByDay(departures);

  return (
    <section className="pfa-bus">
      {routes.length > 0 && (
        <div className="pfa-bus-routes">
          {routes.map(r => <RouteBadge key={r.key} route={r} />)}
        </div>
      )}

      {state.loading && <div className="pfa-places-note">Checking the timetable…</div>}
      {state.error && <div className="pfa-bus-err">{state.error}</div>}

      {data && !departures.length && (
        // An answer, not an empty state. One real platform on campus returned
        // nothing for the whole week during testing, and "no scheduled service"
        // is the useful thing to say about it.
        <div className="pfa-places-note">
          No scheduled departures{data.exhausted ? ' in the next week' : ''}.
          {routes.length > 0 && ' The routes above serve this stop at other times.'}
        </div>
      )}

      {groups.map(group => (
        <div key={group.key} className="pfa-bus-day">
          <div className="pfa-bus-dayhead">{group.label}</div>
          {group.departures.map(d => {
            const late = d.cancelled ? null : lateness(d.scheduled, d.estimated);
            return (
              <div key={d.key}
                   className={`pfa-bus-row${d.cancelled ? ' cancelled' : ''}${isImminent(d) ? ' soon' : ''}`}>
                {d.route && <RouteBadge route={d.route} />}
                <span className="pfa-bus-dest">
                  {d.variant || d.route?.name || ''}
                  {d.cancelled && <span className="pfa-bus-late"> cancelled</span>}
                  {late && <span className="pfa-bus-late"> {late}</span>}
                </span>
                {/* Both readings, because they answer different questions:
                    "should I run" wants the minutes, "can I finish this coffee"
                    wants the clock. */}
                <span className="pfa-bus-when">
                  <strong>{inMinutes(d.at || d.scheduled)}</strong>
                  <span className="pfa-bus-clock">{clock(d.at || d.scheduled)}</span>
                </span>
              </div>
            );
          })}
        </div>
      ))}

      {data?.stop && stopWhere(data.stop) && (
        <div className="pfa-bus-foot">
          Stop #{data.stop.number ?? stopKey} · {stopWhere(data.stop)}
        </div>
      )}
      {!data?.stop && (
        <div className="pfa-bus-foot">Stop #{stopKey}</div>
      )}
    </section>
  );
}
